import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';

/**
 * Modale centrée sur le viewport via createPortal — norme stack standard.
 *
 * - Backdrop fixed inset:0 avec backdrop-filter: blur(4px)
 * - Fermeture : Échap, clic backdrop, bouton ✕
 * - Focus piégé au sein de la modale au montage
 */
export default function Modal({ open, onClose, title, children, footer, size = 'md' }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    // Focus dans la modale
    dialogRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const widths = { sm: '400px', md: '600px', lg: '900px', xl: '1200px' };
  const maxWidth = widths[size] ?? widths.md;

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.45)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--card-bg)',
          color: 'var(--text)',
          borderRadius: 'var(--radius-md)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
          width: '100%',
          maxWidth,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1rem 1.5rem',
            borderBottom: '1px solid var(--glass-border)',
          }}
        >
          <h2 id="modal-title" style={{ margin: 0, fontSize: '1.1rem', color: 'var(--primary)' }}>
            {title}
          </h2>
          <button
            onClick={onClose}
            aria-label="Fermer"
            title="Fermer (Échap)"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-muted)',
              display: 'flex',
              alignItems: 'center',
              padding: '0.25rem',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            <X size={20} />
          </button>
        </header>

        <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>{children}</div>

        {footer && (
          <footer
            style={{
              padding: '1rem 1.5rem',
              borderTop: '1px solid var(--glass-border)',
              display: 'flex',
              gap: '0.5rem',
              justifyContent: 'flex-end',
            }}
          >
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
